import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api';
import './DownloadsPage.css';

const truncate = (str, n) => str && str.length > n ? str.slice(0, n) + '…' : (str || '');

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDatePart(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function formatTimePart(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export default function DownloadsPage() {
  const [files, setFiles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/downloads/list');
      setFiles(res.data.files || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const handleDownload = async (key, filename) => {
    try {
      const res = await apiClient.get(`/api/downloads/${key}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="downloads-page">
      <div className="downloads-header">
        <div className="downloads-title-row">
          <h2 className="downloads-title">Downloads</h2>
          <button className="btn-refresh" onClick={loadList} disabled={loading}>
            {loading ? '⟳' : '⟳'}
          </button>
        </div>
        <div className="downloads-summary">
          <span className="dl-stat">{files.length} backend files</span>
        </div>
      </div>

      {error && <div className="dl-error">{error}</div>}

      {!loading && files.length === 0 && (
        <div className="dl-empty">No files available.</div>
      )}

      <div className="downloads-table-wrap">
        <table className="downloads-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Type</th>
              <th>Size</th>
              <th>Last Modified</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {files.map(f => (
              <tr key={f.key} className={!f.exists ? 'row-missing' : ''}>
                <td className="dl-filename">
                  <span className="dl-label" title={f.label}>{truncate(f.label, 15)}</span>
                  <button
                    className="dl-mobile-link"
                    disabled={!f.exists}
                    onClick={() => handleDownload(f.key, f.filename)}
                    title={f.filename}
                  >
                    ↓ {truncate(f.filename, 13)}
                  </button>
                </td>
                <td>
                  <span className="dl-type-badge dl-type-static">Data</span>
                </td>
                <td>{formatBytes(f.size)}</td>
                <td>
                  <div className="dl-modified-cell">
                    <span className="dl-date">{formatDatePart(f.modifiedAt)}</span>
                    <span className="dl-time">{formatTimePart(f.modifiedAt)}</span>
                  </div>
                </td>
                <td>
                  <span className={`dl-status ${f.exists ? 'status-complete' : 'status-missing'}`}>
                    {f.exists ? 'Available' : 'Missing'}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-download"
                    data-mobile-icon="↓"
                    disabled={!f.exists}
                    onClick={() => handleDownload(f.key, f.filename)}
                  >
                    ↓ Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
